import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { normaliseForSearch } from '../../utils/diacritics';
import './DictionarySearch.css';

/**
 * DictionarySearch — type-ahead search against dictionary entries.
 * Uses canonicalFormNormalised for diacritic-tolerant prefix matching.
 * Supports keyboard navigation (↑/↓/Enter/Escape) in the results dropdown.
 *
 * Props:
 *   onSelect(entry) — called when the user selects a result
 *   onNotFound(term) — called when user confirms "not found"
 *   onClear() — called when user clears the search
 */
function DictionarySearch({ onSelect, onNotFound, onClear }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const listboxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchTerm.trim().length < 1) {
      setResults([]);
      setShowResults(false);
      setNoResults(false);
      setActiveIndex(-1);
      return;
    }

    // Debounce the search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(searchTerm.trim());
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [results, noResults]);

  const performSearch = async (term) => {
    setLoading(true);
    setNoResults(false);

    const normalised = normaliseForSearch(term);
    // Firestore prefix range query on normalised field
    const endStr = normalised.slice(0, -1) + String.fromCharCode(normalised.charCodeAt(normalised.length - 1) + 1);

    try {
      const q = query(
        collection(db, 'dictionary'),
        where('canonicalFormNormalised', '>=', normalised),
        where('canonicalFormNormalised', '<', endStr),
        orderBy('canonicalFormNormalised'),
        limit(10)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setResults([]);
        setNoResults(true);
      } else {
        const entries = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setResults(entries);
        setNoResults(false);
      }
      setShowResults(true);
    } catch (e) {
      console.error('Dictionary search error:', e);
      setResults([]);
      setNoResults(true);
      setShowResults(true);
    }
    setLoading(false);
  };

  const handleSelect = (entry) => {
    setSearchTerm(entry.canonicalForm);
    setShowResults(false);
    setActiveIndex(-1);
    onSelect(entry);
  };

  const handleConfirmNotFound = () => {
    setShowResults(false);
    setActiveIndex(-1);
    onNotFound(searchTerm.trim());
  };

  const handleKeyDown = (e) => {
    if (!showResults) {
      // If dropdown not open, Enter triggers search
      if (e.key === 'Enter' && searchTerm.trim().length >= 1) {
        performSearch(searchTerm.trim());
      }
      return;
    }

    const totalItems = noResults ? 1 : results.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev + 1;
          return next >= totalItems ? totalItems - 1 : next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev - 1;
          return next < -1 ? -1 : next;
        });
        break;

      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0) {
          if (noResults) {
            // "Add it now?" is focused
            handleConfirmNotFound();
          } else if (activeIndex < results.length) {
            handleSelect(results[activeIndex]);
          }
        } else if (searchTerm.trim().length >= 1) {
          performSearch(searchTerm.trim());
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowResults(false);
        setActiveIndex(-1);
        break;

      default:
        break;
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listboxRef.current) return;
    const items = listboxRef.current.querySelectorAll('[role="option"]');
    if (items[activeIndex]) {
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const getActiveDescendant = () => {
    if (activeIndex < 0) return undefined;
    if (noResults) return 'dict-search-option-add';
    if (activeIndex < results.length) return `dict-search-option-${results[activeIndex].id}`;
    return undefined;
  };

  return (
    <div className="dict-search">
      <div className="dict-search-input-row">
        <input
          ref={inputRef}
          className="dict-search-input"
          type="text"
          placeholder="Search dictionary (e.g. etre, se rendre compte)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search dictionary"
          role="combobox"
          aria-expanded={showResults}
          aria-controls="dict-search-listbox"
          aria-activedescendant={getActiveDescendant()}
          autoComplete="off"
        />
        {searchTerm && (
          <button
            className="dict-search-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setResults([]);
              setShowResults(false);
              setNoResults(false);
              setActiveIndex(-1);
              onClear && onClear();
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >×</button>
        )}
        {loading && <span className="dict-search-spinner" aria-hidden="true" />}
      </div>

      {showResults && (
        <div
          ref={listboxRef}
          id="dict-search-listbox"
          className="dict-search-results"
          role="listbox"
          aria-label="Search results"
        >
          {results.map((entry, index) => (
            <button
              key={entry.id}
              id={`dict-search-option-${entry.id}`}
              className={`dict-search-result-item ${index === activeIndex ? 'dict-search-result-item--active' : ''}`}
              onClick={() => handleSelect(entry)}
              role="option"
              aria-selected={index === activeIndex}
            >
              <span className="dict-search-result-form">{entry.canonicalForm}</span>
              <span className="dict-search-result-type">{entry.wordType}</span>
              <span className="dict-search-result-meaning">{entry.meaningEnglish}</span>
            </button>
          ))}

          {noResults && (
            <div
              id="dict-search-option-add"
              className={`dict-search-not-found ${activeIndex === 0 ? 'dict-search-not-found--active' : ''}`}
              role="option"
              aria-selected={activeIndex === 0}
            >
              <p>"{searchTerm}" not found in dictionary.</p>
              <button
                className="dict-search-add-btn"
                onClick={handleConfirmNotFound}
                tabIndex={-1}
              >
                Add it now?
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DictionarySearch;
